import { PDFArray, PDFDict, PDFName, PDFString } from 'pdf-lib';
import { extractOverlayMetadata } from './overlayMetadata';
import { persistOverlaysToDoc } from './overlayPersistence';

// copyPages copies appearances but not the document's editable-overlay
// sidecar or AcroForm root. Rebuild both after merge/extract/reorder.
export async function copyPageOverlays(source, pageNumbers, offset, prefix) {
  const metadata = await extractOverlayMetadata(source);
  const edits = [];
  const annotations = [];
  for (const [index, pageNum] of pageNumbers.entries()) {
    const remap = (item) => ({ ...item, id: `${prefix}-${index}-${item.id}`, copiedFromId: item.id, pageNum: offset + index + 1,
      ...(item.groupId ? { groupId: `${prefix}-${index}-${item.groupId}` } : {}) });
    edits.push(...(metadata?.edits || []).filter((item) => item.pageNum === pageNum).map(remap));
    annotations.push(...(metadata?.annotations || []).filter((item) => item.pageNum === pageNum).map(remap));
  }
  return { edits, annotations };
}

export async function finishPageAssembly(doc, overlays) {
  const roots = new Map();
  const included = new Set();
  const ancestors = new Map();
  for (const page of doc.getPages()) {
    const annots = page.node.lookupMaybe(PDFName.of('Annots'), PDFArray);
    if (!annots) continue;
    for (let i = 0; i < annots.size(); i++) {
      let ref = annots.get(i);
      let field = doc.context.lookup(ref);
      if (!(field instanceof PDFDict) || field.get(PDFName.of('Subtype')) !== PDFName.of('Widget')) continue;
      field.set(PDFName.of('P'), page.ref);
      included.add(String(ref));
      const visited = new Set();
      while (field.has(PDFName.of('Parent'))) {
        const parentRef = field.get(PDFName.of('Parent'));
        if (visited.has(String(parentRef))) throw new Error('The PDF contains an invalid form field tree.');
        visited.add(String(parentRef));
        ref = parentRef;
        field = doc.context.lookup(ref, PDFDict);
        included.add(String(ref));
        ancestors.set(String(ref), field);
      }
      roots.set(String(ref), ref);
    }
  }
  for (const field of ancestors.values()) {
    const kids = field.lookupMaybe(PDFName.of('Kids'), PDFArray);
    if (!kids) continue;
    for (let i = kids.size() - 1; i >= 0; i--) if (!included.has(String(kids.get(i)))) kids.remove(i);
  }
  if (roots.size) doc.catalog.set(PDFName.of('AcroForm'), doc.context.obj({ Fields: [...roots.values()] }));
  // Identically named fields from different inputs must remain independent.
  // PDF readers otherwise mirror edits between the merged forms.
  const names = new Set();
  for (const field of doc.getForm().getFields()) {
    const name = field.getName();
    let suffix = 2;
    let unique = name;
    while (names.has(unique)) unique = `${name} (${suffix++})`;
    if (unique !== name) {
      const partial = field.acroField.getPartialName() || name;
      field.acroField.setPartialName(`${partial}${unique.slice(name.length)}`);
    }
    names.add(unique);
  }
  // A legacy input may have our standard annotations but no sidecar.
  // Keep those appearances as imported annotations when another input has
  // editable metadata; the persistence pass must not purge them.
  for (const [index, page] of doc.getPages().entries()) {
    const knownIds = new Set([...overlays.edits, ...overlays.annotations]
      .filter((item) => item.pageNum === index + 1).map((item) => item.copiedFromId || item.id));
    const annots = page.node.lookupMaybe(PDFName.of('Annots'), PDFArray);
    if (!annots) continue;
    for (let i = 0; i < annots.size(); i++) {
      const annot = doc.context.lookup(annots.get(i), PDFDict);
      const name = annot.get(PDFName.of('NM'))?.decodeText?.();
      if (name?.startsWith('scrivar-ff:') || name?.startsWith('scrivar:') && !knownIds.has(name.slice(8))) {
        annot.set(PDFName.of('NM'), PDFString.of(`imported:${name}`));
      }
    }
  }
  for (const item of [...overlays.edits, ...overlays.annotations]) delete item.copiedFromId;
  if (overlays.edits.length || overlays.annotations.length) await persistOverlaysToDoc(doc, overlays.edits, overlays.annotations);
  return doc.save();
}
